import { CalculatePricePromotion, CalculatedPrice } from '@teko/cart/v2';
import {
  BenefitItems,
  BenefitVoucher,
  Brand,
  CookingRecipes,
  DiscoveryAttribute,
  DiscoveryProduct,
  DiscoveryPromotion,
  DiscoverySellerProvider,
  ProductDetailAttributeGroup,
  ProductDetailAttributeSet,
  ProductDetailProductGroupVariant,
  ProductDetailProductGroupVariantAttrValue,
  ProductDetailSeoInfo,
  ProductInfoCategory,
  ProductOptions,
  ProductProvider,
  ProductStatus,
  SearchProductPagination,
} from '@teko/discovery';
import { AddToCartProps } from '@teko/ecommerce';
import { SOURCE_VIDEO } from '@teko/teko-ecommerce';
import { ProductCardButton, ProductCardProps } from '@teko/types/ecommerce';
import { CartContextStateV2, IBuildPCV2ContextState } from 'contexts';
import { Breadcrumb } from 'modules/search-page/shared';
import { IGetProductReviewResponse } from 'types';
import { BannerEntity } from './entities';

export interface ServerProduct {
  products?: DiscoveryProduct[];
  pagination?: SearchProductPagination;
}

export interface ProductResponse {
  product: DiscoveryProduct;
  relatedProducts: ProductCardProps[];
  priceAndPromotions?: CalculatedPrice;
  breadcrumbs?: Breadcrumb;
  rating?: IGetProductReviewResponse;
}

export interface ConfigurationOption {
  value: string;
  optionId: number;
  thumbnailUrl: string;
  visible: boolean;
  selected: boolean;
}

export interface ConvertedConfiguration {
  id: number;
  code: string;
  name: string;
  optionType: string;
  options: ConfigurationOption[];
}

export interface ProductImage {
  src: string;
  alt: string;
}

export interface Video {
  source: SOURCE_VIDEO;
  url: string;
  title?: string;
  description?: string;
}

export interface ConvertedProduct {
  sku: string;
  sellerSku: string;
  tags?: string[];
  flashSale?: {
    total: number;
    used: number;
    endAt: string;
    type: string;
    endTimestampSec: string;
  };
  status?: ProductStatus;
  images: ProductImage[];
  videos?: Video[];
  title: {
    name: string;
    status?: {
      value: string;
      color: string;
    };
  };
  name: string;
  displayName: string;
  price: {
    price?: string | number;
    supplierRetailPrice?: string | number;
    listedPrice?: string | number;
    terminalPrice?: string | number;
    sellPrice?: string | number;
    discount?: string | number;
    priceWithoutSelectionPromotion?: number;
    discountAmount?: string | number;
    promotions?: CalculatePricePromotion[];
  };
  seoInfo?: ProductDetailSeoInfo;
  brand: Brand;
  brands: Brand[];
  categories: ProductInfoCategory[];
  masterCategories: ProductInfoCategory[];
  attributeGroups?: ProductDetailAttributeGroup[];
  attributes?: DiscoveryAttribute[];
  needManageStock: boolean;
  totalAvailable?: number;
  attributeSet?: ProductDetailAttributeSet;
  provider?: ProductProvider;
  seller?: DiscoverySellerProvider;
  selected?: ProductDetailProductGroupVariantAttrValue[];
  orderCouponPromotions?: DiscoveryPromotion[];
  partOrderPromotions?: DiscoveryPromotion[];
  defaultPromotions?: DiscoveryPromotion[];
  selectionPromotions?: DiscoveryPromotion[];
  productGroup?: {
    id: number;
    name: string;
    visible: string;
    variants?: ProductDetailProductGroupVariant[];
    configurations?: ConvertedConfiguration[];
  };
  isBundle?: boolean;
  addToCart?: AddToCartProps;
  save?: number;
  saveWithoutSelectionPromotion?: number;
  selectPromotionId?: number;
  isAlcoholicCategory?: boolean;
  uomName?: string;
  uomRatio?: number;
  uomCode?: string;
  barcode?: string;
  clearanceTypeDescription?: string;
  productOptions?: ProductOptions;
  breadcrumbs?: Breadcrumb;
  cookingRecipes?: CookingRecipes[];
}

export interface OptionConvertProductCart {
  frames?: BannerEntity[]; // from client
  cartV2?: CartContextStateV2; // cart-context
  buildPCV2?: IBuildPCV2ContextState; // buildpc v2 context
  showUom?: boolean; // from global data
  contactLink?: string; // from global data
  showCtaButton?: boolean; // from global data
  showCtaButtons?: boolean;
  showNewProductGift?: boolean;
  hideHighlightAttribute?: boolean;
  isMobile?: boolean;
  showCtaButtonsWithQuantity?: boolean;
  showCtaButtonsRelevantProducts?: boolean;
  enableTrackOpenInNewTab?: boolean;
  wishlistButton?: ProductCardButton;
  bestComboDiscount?: string;
  benefitLabel?: string | null;
}

export interface ComboItem {
  type: 'autogen' | 'dynamic';
  supplierRetailPrice: number;
  latestPrice: number;
  promotion?: DiscoveryPromotion;
  promotionId?: number;
  items: {
    quantity: number;
    product: DiscoveryProduct;
  }[];
}

export interface ComboProduct {
  sku: string;
  sellerSku: string;
  image: string;
  name: string;
  selectedVariant: string[];
  quantity: number;
  supplierRetailPrice: number;
  latestPrice: number;
  totalAvailable: number | null;
  selectionPromotionId?: number;
  selectionPromotion: DiscoveryPromotion | null;
  selectionPromotions: DiscoveryPromotion[];
  defaultPromotions: DiscoveryPromotion[];
  allowSelectPromotion?: boolean;
  gifts: BenefitItems[];
  vouchers: BenefitVoucher[];
  saveWithoutSelectionPromotion?: number;
  productConfigurations?: ConvertedConfiguration[];
  priceWithoutSelectionPromotion?: number;
  uomName?: string;
}

export enum PromotionApplyOnEnum {
  product = 'product',
  partOrder = 'part-order',
}

export interface IDataConvertLinkProduct {
  name: string;
  sku: string;
}
export interface ILinkProduct {
  as: string;
  href: {
    pathname: string;
    query: {
      sku: string;
    };
  };
  passHref: boolean;
}

export interface ProductRedisBreadCrumbs {
  level: number;
  slug: string;
  breadcrumbs: Array<{
    url?: string;
    label: string;
  }>;
}
